import { createQueryString } from '@sixfold/app-utils';
import { InfiniteTable, InfiniteTableColumns, InfiniteTableSortBy, Stack, Text } from '@sixfold/common-ui';
import { notNil } from '@sixfold/typed-primitives';
import React, { useCallback, useMemo, useState } from 'react';
import { NavLink } from 'react-router';

import { capitalize } from '../../lib/format';
import { Routes } from '../../routes';

export interface CompanyMetricRow {
  company: Company | null;
  transport_count: number;
  allocated_count: number;
  percent_allocated: string;
  visibility_index: string;
}

interface Company {
  company_id: string;
  company_name: string | null;
}

interface ParentCompany {
  company_id: string;
}

export interface MetricsViewDataProps {
  company: ParentCompany | null;
  rows: CompanyMetricRow[];
}

export interface MetricsViewProps {
  show: 'carriers' | 'customers';
}

interface MetricTableRow extends CompanyMetricRow {
  id: string;
  company_name_sort: string;
}

const getPartnershipIds = (company: ParentCompany, partner: Company | null, show: 'carriers' | 'customers') => ({
  carrier_id: show === 'customers' ? company.company_id : partner !== null && partner.company_id,
  shipper_id: show === 'customers' ? partner !== null && partner.company_id : company.company_id,
});

const getSortValue = (row: MetricTableRow, key: keyof MetricTableRow): string | number => {
  switch (key) {
    case 'company_name_sort':
      return row.company_name_sort;
    case 'transport_count':
      return row.transport_count;
    case 'allocated_count':
      return row.allocated_count;
    case 'percent_allocated':
      return row.percent_allocated;
    case 'visibility_index':
      return row.visibility_index;
    default:
      return '';
  }
};

const sortData = (data: MetricTableRow[], sortBy: InfiniteTableSortBy<keyof MetricTableRow>): MetricTableRow[] => {
  return [...data].sort((a, b) => {
    const aVal = getSortValue(a, sortBy.columnId);
    const bVal = getSortValue(b, sortBy.columnId);
    const cmp =
      typeof aVal === 'number' && typeof bVal === 'number' ? aVal - bVal : String(aVal).localeCompare(String(bVal));
    return sortBy.order === 'ASC' ? cmp : -cmp;
  });
};

export const MetricsView: React.FC<MetricsViewDataProps & MetricsViewProps> = ({ rows, company, show }) => {
  const [sortBy, setSortBy] = useState<InfiniteTableSortBy<keyof MetricTableRow>>({
    columnId: 'transport_count',
    order: 'ASC',
  });

  const [scrollParent, setScrollParent] = useState<HTMLElement | null>(null);

  const containerRefCallback = useCallback((node: HTMLDivElement | null) => {
    if (node === null) return;
    let el: HTMLElement | null = node.parentElement;
    while (el !== null) {
      const { overflow, overflowY } = getComputedStyle(el);
      if (overflow === 'auto' || overflow === 'scroll' || overflowY === 'auto' || overflowY === 'scroll') {
        setScrollParent(el);
        return;
      }
      el = el.parentElement;
    }
  }, []);

  const tableData = useMemo(
    () =>
      sortData(
        rows.map((r, idx) => ({
          ...r,
          id: r.company?.company_id ?? String(idx),
          company_name_sort: r.company?.company_name ?? '',
        })),
        sortBy,
      ),
    [rows, sortBy],
  );

  const columns: InfiniteTableColumns<MetricTableRow> = useMemo(
    () => [
      {
        id: 'company_name_sort',
        title: 'Company',
        width: '30%',
        minWidth: '150px',
        isUserSortable: true,
        cellContent: ({ row }) => {
          const companyName = row.company?.company_name;
          const companyId = row.company?.company_id;
          return notNil(companyName) && notNil(companyId) ? (
            <NavLink to={Routes.Company.generatePath({ company_id: companyId })}>{companyName}</NavLink>
          ) : (
            <>-</>
          );
        },
      },
      {
        id: 'transport_count',
        title: 'Transports',
        width: '18%',
        minWidth: '100px',
        isUserSortable: true,
        cellContent: ({ row }) => {
          if (company === null) return <>-</>;
          const filterQuery = createQueryString(getPartnershipIds(company, row.company, show));
          return <NavLink to={`${Routes.Tours.generatePath({})}?${filterQuery}`}>{row.transport_count}</NavLink>;
        },
      },
      {
        id: 'allocated_count',
        title: 'With vehicle allocated',
        width: '18%',
        minWidth: '130px',
        isUserSortable: true,
        cellContent: ({ row }) => {
          if (company === null) return <>-</>;
          const filterQuery = createQueryString(getPartnershipIds(company, row.company, show));
          return (
            <NavLink to={`${Routes.Tours.generatePath({})}?${filterQuery}&status=allocated`}>
              {row.allocated_count}
            </NavLink>
          );
        },
      },
      {
        id: 'percent_allocated',
        title: '% Allocated',
        width: '17%',
        minWidth: '100px',
        isUserSortable: true,
        cellContent: ({ row }) => <>{row.percent_allocated}</>,
      },
      {
        id: 'visibility_index',
        title: 'Visibility index',
        width: '17%',
        minWidth: '100px',
        isUserSortable: true,
        cellContent: ({ row }) => <>{row.visibility_index}</>,
      },
    ],
    [company, show],
  );

  if (tableData.length === 0) {
    return (
      <Stack direction="column" gap="medium">
        <h2 style={{ margin: 0 }}>{capitalize(show)}</h2>
        <Text>{show === 'carriers' ? 'No carriers' : 'No customers'}</Text>
      </Stack>
    );
  }

  return (
    <Stack direction="column" gap="medium">
      <h2 style={{ margin: 0 }}>{capitalize(show)}</h2>
      <div className="sub-table-scroll-container" style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <div ref={containerRefCallback}>
          {scrollParent !== null && (
            <InfiniteTable<MetricTableRow>
              columns={columns}
              data={tableData}
              sortBy={sortBy}
              onSortByChange={setSortBy}
              scrollParent={scrollParent}
              className="compact-table"
            />
          )}
        </div>
      </div>
    </Stack>
  );
};
